/**
 * Admin → one organization: profile (view by default, edit as an explicit
 * mode), properties with an expandable unit drawer, users with activation and
 * per-property access. All contracts carried over from the static page:
 * billing NULL means same-as-mailing and the checkbox clears rather than
 * copies; the unit drawer's address copy-forwards; the set-password link is
 * shown once and never stored.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, designationLabel, money, type Property, type Unit } from '../../api';
import {
  adminApi,
  ROLE_LABEL,
  type Assignment,
  type CompanyDetail,
  type CompanyUser,
} from '../../adminApi';
import { AdminTopCrumb } from '../../adminShell';
import { AddressInput } from '../../components/AddressInput';
import { InvitePanel } from '../../components/InvitePanel';
import { StateSelect } from '../../components/StateSelect';

function postalLines(l1: string | null, l2: string | null, city: string | null, state: string | null, zip: string | null): string[] {
  const lines: string[] = [];
  if (l1) lines.push(l1);
  if (l2) lines.push(l2);
  const cityLine = [[city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(' ');
  if (cityLine.trim()) lines.push(cityLine.trim());
  return lines;
}

function Postal({ lines, empty }: { lines: string[]; empty?: ReactNode }) {
  if (!lines.length) return <>{empty ?? <span className="dim">—</span>}</>;
  return (
    <>
      {lines.map((line, i) => (
        <span key={i}>
          {i > 0 && <br />}
          {line}
        </span>
      ))}
    </>
  );
}

export function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const companyId = id!;
  const navigate = useNavigate();

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [invite, setInvite] = useState<{ email: string; link: string; account?: string } | null>(null);

  function loadCompany() {
    return adminApi
      .company(companyId)
      .then((d) => setCompany(d.company))
      .catch(() => navigate('/admin', { replace: true }));
  }

  useEffect(() => {
    void loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (!company) return null;

  return (
    <>
      <AdminTopCrumb>
        <Link to="/admin">Organizations</Link>
        <span className="sep">/</span>
        <b>{company.name}</b>
      </AdminTopCrumb>

      <main className="page" id="main">
        <div className="page__head">
          <div>
            <h1>{company.name}</h1>
            <p className="sub">{[company.city, company.state].filter(Boolean).join(', ') || 'No address on file yet'}</p>
          </div>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 14 }}>
          <div className="kpi">
            <b>{company.propertyCount}</b>
            <span>Properties</span>
          </div>
          <div className="kpi">
            <b>{company.unitCount}</b>
            <span>Units</span>
          </div>
          <div className="kpi">
            <b>{company.userCount}</b>
            <span>Users</span>
          </div>
          <div className="kpi">
            <b style={{ fontSize: 16 }}>{company.status === 'active' ? 'Active' : company.status}</b>
            <span>Status</span>
          </div>
        </div>

        {invite && (
          <InvitePanel email={invite.email} link={invite.link} account={invite.account} onDone={() => setInvite(null)} />
        )}

        <ProfilePanel company={company} onSaved={() => void loadCompany()} />
        <PropertiesPanel companyId={companyId} onChanged={() => void loadCompany()} />
        <UsersPanel
          companyId={companyId}
          onInvite={setInvite}
          onChanged={() => void loadCompany()}
        />
      </main>
    </>
  );
}

/* --- Business profile ------------------------------------------------------ */

function ProfilePanel({ company, onSaved }: { company: CompanyDetail; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const mailing = postalLines(company.addressLine1, company.addressLine2, company.city, company.state, company.postalCode);
  const billing = postalLines(
    company.billingAddressLine1,
    company.billingAddressLine2,
    company.billingCity,
    company.billingState,
    company.billingPostalCode,
  );

  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div className="panel__head">
        <h2>Business profile</h2>
        <div className="spacer" />
        {saved && <span className="dim">Saved</span>}
        {!editing && (
          <button className="btn btn--sm" onClick={() => setEditing(true)}>
            Edit profile
          </button>
        )}
      </div>

      {!editing ? (
        <div className="panel__body">
          <table className="tbl">
            <tbody>
              <tr>
                <td className="dim" style={{ width: 150 }}>
                  Name
                </td>
                <td>{company.name}</td>
              </tr>
              <tr>
                <td className="dim">Mailing address</td>
                <td>
                  <Postal lines={mailing} />
                </td>
              </tr>
              <tr>
                <td className="dim">Billing address</td>
                <td>
                  <Postal lines={billing} empty={<span className="dim">Same as mailing</span>} />
                </td>
              </tr>
              <tr>
                <td className="dim">Phone</td>
                <td>{company.phone ?? <span className="dim">—</span>}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <ProfileForm
          company={company}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
            onSaved();
          }}
        />
      )}
    </div>
  );
}

function ProfileForm({ company, onCancel, onSaved }: { company: CompanyDetail; onCancel: () => void; onSaved: () => void }) {
  const [fields, setFields] = useState<Record<string, string>>({
    name: company.name,
    address_line1: company.addressLine1 ?? '',
    address_line2: company.addressLine2 ?? '',
    city: company.city ?? '',
    state: company.state ?? '',
    postal_code: company.postalCode ?? '',
    billing_address_line1: company.billingAddressLine1 ?? '',
    billing_address_line2: company.billingAddressLine2 ?? '',
    billing_city: company.billingCity ?? '',
    billing_state: company.billingState ?? '',
    billing_postal_code: company.billingPostalCode ?? '',
    phone: company.phone ?? '',
  });
  const [billingSame, setBillingSame] = useState(!company.billingAddressLine1 && !company.billingCity);
  const [error, setError] = useState<string | null>(null);

  const set = (key: string) => (value: string) => setFields((f) => ({ ...f, [key]: value }));
  const bind = (key: string) => ({
    value: fields[key] ?? '',
    onChange: (e: { target: { value: string } }) => set(key)(e.target.value),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const payload: Record<string, string> = {};
    for (const [key, value] of Object.entries(fields)) {
      // "Same as mailing" sends billing EMPTY — the database nullifs, and NULL
      // means same-as. Clearing, never copying.
      payload[key] = key.startsWith('billing_') && billingSame ? '' : value.trim();
    }

    adminApi
      .updateCompany(company.id, payload)
      .then(onSaved)
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'name_required'
            ? 'The organization name cannot be empty.'
            : 'Could not save the profile.',
        );
      });
  }

  return (
    <div className="panel__body">
      <form className="form" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="p-name">Organization name</label>
          <input id="p-name" type="text" required {...bind('name')} />
        </div>
        <div className="field--row">
          <div className="field">
            <label htmlFor="p-address1">Mailing address</label>
            <AddressInput
              id="p-address1"
              value={fields.address_line1 ?? ''}
              onChange={set('address_line1')}
              onPlace={(p) =>
                setFields((f) => ({
                  ...f,
                  address_line1: p.line1 || (f.address_line1 ?? ''),
                  city: p.city || (f.city ?? ''),
                  state: p.state || (f.state ?? ''),
                  postal_code: p.zip || (f.postal_code ?? ''),
                }))
              }
            />
          </div>
          <div className="field">
            <label htmlFor="p-address2">Suite / line 2</label>
            <input id="p-address2" type="text" {...bind('address_line2')} />
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label htmlFor="p-city">City</label>
            <input id="p-city" type="text" {...bind('city')} />
          </div>
          <div className="field--row">
            <div className="field">
              <label htmlFor="p-state">State</label>
              <StateSelect id="p-state" {...bind('state')} />
            </div>
            <div className="field">
              <label htmlFor="p-zip">ZIP</label>
              <input id="p-zip" type="text" inputMode="numeric" {...bind('postal_code')} />
            </div>
          </div>
        </div>

        <label className="check" style={{ margin: '4px 0' }}>
          <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} /> Billing address is
          the same as mailing
        </label>

        {!billingSame && (
          <>
            <div className="field--row">
              <div className="field">
                <label htmlFor="p-baddress1">Billing address</label>
                <AddressInput
                  id="p-baddress1"
                  value={fields.billing_address_line1 ?? ''}
                  onChange={set('billing_address_line1')}
                  onPlace={(p) =>
                    setFields((f) => ({
                      ...f,
                      billing_address_line1: p.line1 || (f.billing_address_line1 ?? ''),
                      billing_city: p.city || (f.billing_city ?? ''),
                      billing_state: p.state || (f.billing_state ?? ''),
                      billing_postal_code: p.zip || (f.billing_postal_code ?? ''),
                    }))
                  }
                />
              </div>
              <div className="field">
                <label htmlFor="p-baddress2">Suite / line 2</label>
                <input id="p-baddress2" type="text" {...bind('billing_address_line2')} />
              </div>
            </div>
            <div className="field--row">
              <div className="field">
                <label htmlFor="p-bcity">City</label>
                <input id="p-bcity" type="text" {...bind('billing_city')} />
              </div>
              <div className="field--row">
                <div className="field">
                  <label htmlFor="p-bstate">State</label>
                  <StateSelect id="p-bstate" {...bind('billing_state')} />
                </div>
                <div className="field">
                  <label htmlFor="p-bzip">ZIP</label>
                  <input id="p-bzip" type="text" inputMode="numeric" {...bind('billing_postal_code')} />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="field" style={{ maxWidth: 280 }}>
          <label htmlFor="p-phone">Phone</label>
          <input id="p-phone" type="tel" {...bind('phone')} />
        </div>

        {error && <p className="login__error">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primary" type="submit">
            Save profile
          </button>
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

/* --- Properties + unit drawer ---------------------------------------------- */

function PropertiesPanel({ companyId, onChanged }: { companyId: string; onChanged: () => void }) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function load() {
    return adminApi.properties(companyId).then((d) => setProperties(d.properties));
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="panel" style={{ marginBottom: 14 }}>
      <div className="panel__head">
        <h2>Properties</h2>
        <div className="spacer" />
        <button className="btn btn--sm btn--primary" onClick={() => setCreating(true)}>
          New property
        </button>
      </div>

      {creating && (
        <AdminNewProperty
          companyId={companyId}
          onDone={() => {
            setCreating(false);
            void load().then(onChanged);
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <div className="table-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th />
              <th>Property</th>
              <th>City</th>
              <th>County</th>
              <th>State #</th>
              <th>Units</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {properties.length === 0 && (
              <tr>
                <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                  No properties yet.
                </td>
              </tr>
            )}
            {properties.map((p) => (
              <PropertyRow
                key={p.id}
                property={p}
                open={open === p.id}
                onToggle={() => setOpen(open === p.id ? null : p.id)}
                onUnitAdded={() => void load().then(onChanged)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel__foot">Expand a property to see its profile and manage units.</div>
    </div>
  );
}

function PropertyRow({
  property,
  open,
  onToggle,
  onUnitAdded,
}: {
  property: Property;
  open: boolean;
  onToggle: () => void;
  onUnitAdded: () => void;
}) {
  return (
    <>
      <tr>
        <td style={{ width: 28 }}>
          <button className="iconbtn" style={{ width: 22, height: 22 }} aria-label="Show details" onClick={onToggle}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: open ? undefined : 'rotate(-90deg)' }}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </td>
        <td className="strong">{property.name}</td>
        <td className="dim">{property.city ?? '—'}</td>
        <td className="dim">{property.county ?? '—'}</td>
        <td className="dim">{property.stateProjectNumber ?? '—'}</td>
        <td className="num">
          {property.unitCount}
          {property.totalUnits ? ` / ${property.totalUnits}` : ''}
        </td>
        <td>
          <span className={`tag tag--${property.status === 'active' ? 'ok' : 'grey'}`}>{property.status}</span>
        </td>
      </tr>
      {open && (
        <tr className="admin__drawer">
          <td colSpan={7} style={{ padding: 0 }}>
            <PropertyDrawer property={property} onUnitAdded={onUnitAdded} />
          </td>
        </tr>
      )}
    </>
  );
}

function PropertyDrawer({ property, onUnitAdded }: { property: Property; onUnitAdded: () => void }) {
  const [units, setUnits] = useState<Unit[] | null>(null);
  // Copy-forward: last added address wins, else the property's own.
  const [address, setAddress] = useState(property.addressLine1 ?? '');
  const [unitNumber, setUnitNumber] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [sqft, setSqft] = useState('');
  const [designation, setDesignation] = useState('');
  const [rent, setRent] = useState('');
  const [error, setError] = useState<string | null>(null);

  function load() {
    return adminApi.units(property.id).then((d) => setUnits(d.units));
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  const details: Array<[string, ReactNode]> = [
    [
      'Address',
      postalLines(property.addressLine1, property.addressLine2, property.city, property.state, property.postalCode).length ? (
        <Postal lines={postalLines(property.addressLine1, property.addressLine2, property.city, property.state, property.postalCode)} />
      ) : null,
    ],
    ['County', property.county],
    ['Phone', property.phone],
    ['Fax', property.fax],
    ['Owner entity', property.ownerLlcName],
    ['Owner EIN', property.ownerEin],
    ['State project #', property.stateProjectNumber],
    ['Year built', property.yearBuilt],
  ];
  const present = details.filter(([, v]) => v);

  function addUnit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body: Record<string, unknown> = { unit_number: unitNumber.trim() };
    if (address.trim()) body.address_line1 = address.trim();
    if (bedrooms) body.bedrooms = Number(bedrooms);
    if (sqft) body.square_feet = Number(sqft);
    if (designation === 'market') body.is_market_rate = true;
    else if (designation) body.designation = Number(designation);
    if (rent) body.monthly_rent_cents = Math.round(Number(rent) * 100);

    adminApi
      .createUnit(property.id, body)
      .then(() => {
        setUnitNumber('');
        void load();
        onUnitAdded();
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'unit_exists'
            ? 'That unit number already exists on this property.'
            : 'Could not add the unit.',
        );
      });
  }

  return (
    <div className="admin__drawer-inner">
      {present.length > 0 && (
        <table className="tbl" style={{ background: 'transparent', marginBottom: 12 }}>
          <tbody>
            {present.map(([label, value]) => (
              <tr key={label}>
                <td className="dim" style={{ width: 150 }}>
                  {label}
                </td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {units === null ? (
        <p className="dim" style={{ margin: 0 }}>
          Loading units…
        </p>
      ) : units.length === 0 ? (
        <p className="dim" style={{ margin: '0 0 8px' }}>
          No units yet.
        </p>
      ) : (
        <table className="tbl" style={{ background: 'transparent' }}>
          <thead>
            <tr>
              <th>Unit</th>
              <th>Address</th>
              <th>Bed</th>
              <th>Sq ft</th>
              <th>Designation</th>
              <th>Rent</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {units.map((u) => (
              <tr key={u.id}>
                <td className="strong">{u.unitNumber}</td>
                <td className="dim">{u.addressLine1 ?? '—'}</td>
                <td>{u.bedrooms ?? '—'}</td>
                <td>{u.squareFeet ?? '—'}</td>
                <td>{designationLabel(u)}</td>
                <td className="num">{money(u.monthlyRentCents)}</td>
                <td>
                  <span className={`tag tag--${u.status === 'occupied' ? 'ok' : 'grey'}`}>{u.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="form" style={{ marginTop: 12, maxWidth: 'none' }} onSubmit={addUnit}>
        <div className="field--row">
          <div className="field">
            <label>Unit number</label>
            <input type="text" required placeholder="101" value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} />
          </div>
          <div className="field--row">
            <div className="field">
              <label>Bedrooms</label>
              <input type="number" min={0} max={10} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} />
            </div>
            <div className="field">
              <label>Sq ft</label>
              <input type="number" min={1} value={sqft} onChange={(e) => setSqft(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="field">
          <label>Street address — blank means the property’s address applies</label>
          <AddressInput value={address} onChange={setAddress} onPlace={(p) => p.line1 && setAddress(p.line1)} />
        </div>
        <div className="field--row">
          <div className="field">
            <label>Designation</label>
            <select value={designation} onChange={(e) => setDesignation(e.target.value)}>
              <option value="">LIHTC (program limit)</option>
              {[20, 30, 40, 50, 60, 70, 80].map((n) => (
                <option key={n} value={n}>
                  {n}% AMI
                </option>
              ))}
              <option value="market">Market rate</option>
            </select>
          </div>
          <div className="field">
            <label>Monthly rent ($)</label>
            <input type="number" min={0} step="0.01" value={rent} onChange={(e) => setRent(e.target.value)} />
          </div>
        </div>
        {error && <p className="login__error">{error}</p>}
        <div>
          <button className="btn btn--sm btn--primary" type="submit">
            Add unit
          </button>
        </div>
      </form>
    </div>
  );
}

/* --- Admin new-property form ----------------------------------------------- */

const FUNDING_OPTIONS: Array<[string, string]> = [
  ['lihtc_9', 'LIHTC 9%'],
  ['lihtc_4', 'LIHTC 4% / bond'],
  ['home', 'HOME'],
  ['section_8_pbra', 'Section 8 PBRA'],
];

function AdminNewProperty({ companyId, onDone, onCancel }: { companyId: string; onDone: () => void; onCancel: () => void }) {
  const [fields, setFields] = useState<Record<string, string>>({});
  const [funding, setFunding] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const set = (key: string) => (value: string) => setFields((f) => ({ ...f, [key]: value }));
  const bind = (key: string) => ({
    value: fields[key] ?? '',
    onChange: (e: { target: { value: string } }) => set(key)(e.target.value),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const payload: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value.trim() === '') continue;
      if (key === 'year_built' || key === 'total_units') payload[key] = Number(value);
      else payload[key] = value.trim();
    }
    if (funding.length) payload.funding_types = funding;

    adminApi
      .createProperty(companyId, payload)
      .then(onDone)
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'name_required' ? 'A property name is required.' : 'Could not create the property.',
        );
      });
  }

  return (
    <div className="panel__body">
      <form className="form" style={{ maxWidth: 'none' }} onSubmit={onSubmit}>
        <div className="field--row">
          <div className="field">
            <label>Property name</label>
            <input type="text" required placeholder="Riverbend Terrace" {...bind('name')} />
          </div>
          <div className="field">
            <label>State project number</label>
            <input type="text" placeholder="IL-2024-00123" spellCheck={false} {...bind('state_project_number')} />
            <span className="hint">The monitoring agency’s identifier. Annual reporting is keyed by this.</span>
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label>Address</label>
            <AddressInput
              value={fields.address_line1 ?? ''}
              onChange={set('address_line1')}
              onPlace={(p) =>
                setFields((f) => ({
                  ...f,
                  address_line1: p.line1 || (f.address_line1 ?? ''),
                  city: p.city || (f.city ?? ''),
                  state: p.state || (f.state ?? ''),
                  postal_code: p.zip || (f.postal_code ?? ''),
                  county: p.county || (f.county ?? ''),
                }))
              }
            />
          </div>
          <div className="field">
            <label>Line 2</label>
            <input type="text" {...bind('address_line2')} />
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label>City</label>
            <input type="text" {...bind('city')} />
          </div>
          <div className="field--row">
            <div className="field">
              <label>State</label>
              <StateSelect {...bind('state')} />
            </div>
            <div className="field">
              <label>ZIP</label>
              <input type="text" inputMode="numeric" {...bind('postal_code')} />
            </div>
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label>County</label>
            <input type="text" placeholder="Cook" {...bind('county')} />
            <span className="hint">Income and rent limits are published per county.</span>
          </div>
          <div className="field--row">
            <div className="field">
              <label>Phone</label>
              <input type="tel" {...bind('phone')} />
            </div>
            <div className="field">
              <label>Fax</label>
              <input type="tel" {...bind('fax')} />
            </div>
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label>Owner entity</label>
            <input type="text" placeholder="Riverbend Terrace LLC" {...bind('owner_llc_name')} />
            <span className="hint">The single-asset LLC, distinct from the management company.</span>
          </div>
          <div className="field">
            <label>Owner EIN / TIN</label>
            <input type="text" spellCheck={false} {...bind('owner_ein')} />
            <span className="hint">What Form 8609 and the annual owner certification are filed under.</span>
          </div>
        </div>
        <div className="field--row">
          <div className="field">
            <label>Year built</label>
            <input type="number" min={1800} max={2100} {...bind('year_built')} />
          </div>
          <div className="field--row">
            <div className="field">
              <label>Planned units</label>
              <input type="number" min={0} {...bind('total_units')} />
            </div>
            <div className="field">
              <label>Monitoring agency</label>
              <input type="text" placeholder="IHDA" {...bind('monitoring_agency')} />
            </div>
          </div>
        </div>
        <div className="field">
          <label>Programs</label>
          <div className="check-grid">
            {FUNDING_OPTIONS.map(([value, label]) => (
              <label key={value} className="check">
                <input
                  type="checkbox"
                  checked={funding.includes(value)}
                  onChange={(e) => setFunding((f) => (e.target.checked ? [...f, value] : f.filter((x) => x !== value)))}
                />{' '}
                {label}
              </label>
            ))}
          </div>
        </div>
        {error && <p className="login__error">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primary" type="submit">
            Create property
          </button>
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

/* --- Users + property access ----------------------------------------------- */

function UsersPanel({
  companyId,
  onInvite,
  onChanged,
}: {
  companyId: string;
  onInvite: (invite: { email: string; link: string; account?: string }) => void;
  onChanged: () => void;
}) {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  function load() {
    return Promise.all([adminApi.companyUsers(companyId), adminApi.assignments(companyId), adminApi.properties(companyId)]).then(
      ([u, a, p]) => {
        setUsers(u.users);
        setAssignments(a.assignments);
        setProperties(p.properties);
      },
    );
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function setStatus(userId: string, status: string) {
    void adminApi.setUserStatus(userId, status).then(load);
  }

  function toggleAssignment(userId: string, propertyId: string, assigned: boolean) {
    adminApi
      .setAssignment(userId, propertyId, assigned)
      .then(() => {
        setAssignments((a) =>
          assigned ? [...a, { userId, propertyId, isPrimary: false }] : a.filter((x) => !(x.userId === userId && x.propertyId === propertyId)),
        );
      })
      .catch(() => void load()); // server said no — re-sync
  }

  const STATUS_TAG: Record<string, [string, string]> = {
    active: ['ok', 'Active'],
    invited: ['warn', 'Invited'],
    disabled: ['grey', 'Disabled'],
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <h2>Users</h2>
        <div className="spacer" />
        <button className="btn btn--sm btn--primary" onClick={() => setAdding(true)}>
          Add user
        </button>
      </div>

      {adding && (
        <AddUserForm
          companyId={companyId}
          onDone={(invite) => {
            setAdding(false);
            if (invite) onInvite(invite);
            void load().then(onChanged);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="table-scroll">
        <table className="tbl">
          <thead>
            <tr>
              <th />
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="dim" style={{ padding: '22px 13px' }}>
                  No users yet. Add the first one above.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const tag = STATUS_TAG[u.status] ?? ['grey', u.status];
              const isOpen = open === u.id;
              return (
                <FragmentRow key={u.id}>
                  <tr>
                    <td style={{ width: 28 }}>
                      <button
                        className="iconbtn"
                        style={{ width: 22, height: 22 }}
                        aria-label="Property access"
                        onClick={() => setOpen(isOpen ? null : u.id)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: isOpen ? undefined : 'rotate(-90deg)' }}>
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </td>
                    <td className="strong">{u.name}</td>
                    <td className="dim">{u.email}</td>
                    <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                    <td>
                      <span className={`tag tag--${tag[0]}`}>{tag[1]}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {u.status === 'invited' ? (
                        <button className="btn btn--sm" onClick={() => setStatus(u.id, 'active')}>
                          Activate
                        </button>
                      ) : u.status === 'active' ? (
                        <button className="btn btn--sm btn--danger" onClick={() => setStatus(u.id, 'disabled')}>
                          Disable
                        </button>
                      ) : (
                        <button className="btn btn--sm" onClick={() => setStatus(u.id, 'active')}>
                          Re-enable
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="admin__drawer">
                      <td colSpan={6} style={{ padding: 0 }}>
                        <div className="admin__drawer-inner">
                          {properties.length === 0 ? (
                            <p className="dim" style={{ margin: 0 }}>
                              Create a property first — access is granted per property.
                            </p>
                          ) : (
                            <>
                              <p className="dim" style={{ margin: '0 0 8px' }}>
                                Properties {u.name} can act on:
                              </p>
                              <div className="check-grid">
                                {properties.map((p) => {
                                  const assigned = assignments.some((a) => a.userId === u.id && a.propertyId === p.id);
                                  return (
                                    <label key={p.id} className="check">
                                      <input
                                        type="checkbox"
                                        checked={assigned}
                                        onChange={(e) => toggleAssignment(u.id, p.id, e.target.checked)}
                                      />{' '}
                                      {p.name}
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="panel__foot">
        Expand a user to choose which properties they can act on. Every change here is written to the organization’s own audit
        log.
      </div>
    </div>
  );
}

// React fragments cannot carry keys through .map with adjacent <tr>s cleanly in
// some tooling; a pass-through component keeps the table valid.
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function AddUserForm({
  companyId,
  onDone,
  onCancel,
}: {
  companyId: string;
  onDone: (invite: { email: string; link: string; account?: string } | null) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('site_coordinator');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    adminApi
      .createUser(companyId, { name: name.trim(), email: email.trim(), role })
      .then((result) => {
        onDone(result.setPasswordLink ? { email: email.trim(), link: result.setPasswordLink, account: result.idpAccount } : null);
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'user_exists'
            ? 'That email already belongs to another user.'
            : 'Could not add the user.',
        );
        setBusy(false);
      });
  }

  return (
    <div className="panel__body">
      <form className="form" onSubmit={onSubmit}>
        <div className="field--row">
          <div className="field">
            <label htmlFor="nu-name">Full name</label>
            <input id="nu-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="nu-email">Email</label>
            <input id="nu-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="nu-role">Role</label>
          <select id="nu-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {Object.entries(ROLE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className="hint">Created as “invited” — activate them below once they have set a password.</span>
        </div>
        {error && <p className="login__error">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Adding…' : 'Add user'}
          </button>
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
